import {
  createMutationStore,
  createQueryStore,
  useQueryClient,
} from '@ferreri/minato-react-utils';
import { useRouter } from 'next/router';
import { useMemo } from 'react';

import { useEstablishmentsList } from '@modules/establishments/establishments.store';
import { Establishment } from '@modules/establishments/establishments.types';
import { useCategoriesList } from '../categories/categories.store';
import { Category } from '../categories/categories.types';
import {
  useCompany,
  useCompanyIdQueryParam,
} from '../companies/companies.store';
import { getCompanyEstablishment } from '../establishments/establishments.client';
import {
  usePublicationEstablishmentsList,
  usePublicationEstablishmentsListComposedGeolocation,
} from '../publication-establishments/publication-establishments.store';
import {
  useCoverPublicationImage,
  usePublicationImages,
} from '../publication-images/publication-images.store';
import {
  getPublication,
  getPublicationAvailable,
  getPublications,
  getPublicationsAvailable,
  getPublicationsConsume,
  getPublicationsFeatured,
  postPublication,
  putPublication,
  putPublicationReview,
} from './publications.client';
import { Publication } from './publications.types';
import { useFilterQueryParams } from '@ferreri/prever-component-lib/nextjs';

export const usePublicationIdQueryParam = () => {
  const router = useRouter();

  return router?.query?.publication_id as string;
};

const usePublication = (publicationId: string, config = null) => {
  return createQueryStore({
    queryKey: ['publication', publicationId],
    queryFn: () => getPublication(publicationId),
    enabled: !!publicationId,
    ...config,
  });
};

const usePublicationFeaturedList = (config = null) => {
  return createQueryStore({
    queryKey: ['publication_list_featured'],
    queryFn: getPublicationsFeatured,
    ...config,
  });
};

export const usePublicationList = (config = null) => {
  return createQueryStore({
    queryKey: ['publication_list'],
    queryFn: getPublications,
    ...config,
  });
};

export const useCreatePublication = (config = null) => {
  return createMutationStore({
    mutationFn: postPublication,
    ...getCombinedConfig(config),
  });
};

export const useUpdatePublication = () => {
  const queryClient = useQueryClient();
  const companyId = useCompanyIdQueryParam();
  const publicationId = usePublicationIdQueryParam();
  const queryKey = ['companies', companyId, 'publications', publicationId];

  return createMutationStore({
    mutationFn: putPublication,
    onMutate: async (updatedPublication) => {
      await queryClient.cancelQueries({ queryKey });

      const previousPublication = queryClient.getQueryData(queryKey);

      queryClient.setQueryData(queryKey, {
        ...previousPublication,
        ...updatedPublication.publication,
      });

      return { previousPublication };
    },
    onError: (err, updatedPublication, context) => {
      queryClient.setQueryData(queryKey, context?.previousPublication);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });
};

export const useUpdatePublicationReview = () => {
  const queryClient = useQueryClient();
  const publicationId = usePublicationIdQueryParam();
  const queryKey = ['publication', publicationId];

  return createMutationStore({
    mutationFn: putPublicationReview,
    onMutate: async (updatedPublication) => {
      await queryClient.cancelQueries({ queryKey });

      const previousPublication = queryClient.getQueryData(queryKey);

      queryClient.setQueryData(queryKey, {
        ...previousPublication,
        ...updatedPublication.publication,
      });

      return { previousPublication };
    },
    onError: (err, updatedPublication, context) => {
      queryClient.setQueryData(queryKey, context?.previousPublication);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });
};

export const usePublicationAvailableList = () => {
  return createQueryStore({
    queryKey: ['publication_list_available'],
    queryFn: getPublicationsAvailable,
  });
};

export const useCurrentPublicationAvailable = () => {
  const publicationId = usePublicationIdQueryParam();
  return createQueryStore({
    queryKey: ['publication_available', publicationId],
    queryFn: () => getPublicationAvailable(publicationId),
    enabled: !!publicationId,
  });
};

export const useCurrentPublicationConsume = () => {
  const publicationId = usePublicationIdQueryParam();
  return createQueryStore({
    queryKey: ['publication_consume', publicationId],
    queryFn: () => getPublicationsConsume(publicationId),
    enabled: !!publicationId,
  });
};

export const useCurrentPublicationImages = () => {
  const query = usePublicationImages();

  if (query.isError || !query?.data?.length) {
    return { ...query, data: [] };
  }

  return query;
};

export const useCurrentCoverPublicationImage = (
  publicationId: string,
  coverImageId: number,
) => {
  const query = useCoverPublicationImage(publicationId, coverImageId);

  if (query.isError || !query?.data?.asset_url) {
    return { ...query, data: {} };
  }

  return query;
};

export const useCurrentPublication = (config = null) => {
  const publicationId = usePublicationIdQueryParam();
  return usePublication(publicationId, config);
};

export const usePublicationCompany = (publication) => {
  return useCompany(publication?.company_id);
};

export const usePublicationEstablishment = (
  establishmentId: string,
  companyId: string,
) => {
  return createQueryStore({
    queryKey: ['establishment', establishmentId, 'company', companyId],
    queryFn: () => getCompanyEstablishment(companyId, establishmentId),
    enabled: !!establishmentId && !!companyId,
  });
};

export const usePublicationCompanyContacts = (publication) => {
  const { data: company, isLoading } = usePublicationCompany(publication);
  const contactFields = [
    'email',
    'phone',
    'web',
    'facebook',
    'instagram',
    'whatsapp',
  ];

  const formattedContacts = useMemo(() => {
    return contactFields?.map((field) => [field, company?.[field]]);
  }, [company]);

  return { data: formattedContacts, isLoading };
};

export const usePublicationCategory = (publication: Publication) => {
  const { data: categories, ...query } = useCategoriesList();

  const category = useMemo(() => {
    return categories?.find(
      (category: Category) => category?.id === publication?.category_id,
    );
  }, [categories, publication]);

  return { data: category, ...query };
};

export const usePublicationAvailableListFromCategoryMap = () => {
  const { data: publicationList, ...query } =
    usePublicationAvailableComposedList();

  const publicationListCategoryMap = useMemo(() => {
    const resultMap = new Map();

    publicationList?.forEach((p) => {
      const categoryId = `${p.category_id}`;
      if (!resultMap.has(categoryId)) {
        resultMap.set(categoryId, []);
      }
      resultMap.get(categoryId).push(p);
    });

    return resultMap;
  }, [publicationList]);

  return { data: publicationListCategoryMap, ...query };
};

export const enum PublicationState {
  APPROVED = 'approved',
  REPROVED = 'reproved',
  ANALYSIS = 'analysis',
  FEATURED = 'featured',
  INACTIVE = 'inactive',
  ACTIVE = 'active',
  EXPIRED = 'expired',
}

export const usePublicationCombinedState = (publication: Publication) => {
  const admin_state = useCurrentPublicationAdminState(publication);
  const state = useCurrentPublicationState(publication);
  return { admin_state, state };
};

export const useCurrentPublicationCombinedState = () => {
  const { data: publication, ...query } = useCurrentPublication();

  const combinedPublication = useMemo(
    () => ({
      ...publication,
      ...usePublicationCombinedState(publication),
    }),
    [publication],
  );

  return { data: combinedPublication, ...query };
};

export const usePublicationListCombinedState = (config = null) => {
  const { data: publicationList, ...query } = usePublicationList(config);

  const publicationListWithCombinedState = useMemo(() => {
    return publicationList?.map((publication: any) => ({
      ...publication,
      ...usePublicationCombinedState(publication),
    }));
  }, [publicationList]);

  return { data: publicationListWithCombinedState, ...query };
};

const useCurrentPublicationState = (publication: Publication) => {
  const isStateInactive = !publication?.is_active && publication?.is_approved;
  const isStateActive = publication?.is_active && publication?.is_approved;

  if (isStateInactive) return PublicationState.INACTIVE;

  if (isStateActive) return PublicationState.ACTIVE;

  return PublicationState.ANALYSIS;
};

const useCurrentPublicationAdminState = (publication: Publication) => {
  const isStateApproved =
    !publication?.admin_feedback && publication?.is_approved;
  const isStateReproved =
    publication?.admin_feedback && !publication?.is_approved;

  if (isStateApproved) return PublicationState.APPROVED;

  if (isStateReproved) return PublicationState.REPROVED;

  return PublicationState.ANALYSIS;
};

export const useFilteredPublicationList = (filters = []) => {
  const { data: publicationListCombined, isLoading: isPublicationListLoading } =
    usePublicationListCombinedState();
  const { data: combinedWithCity, isLoading: isFilterCityLoading } =
    usePublicationFromCity(publicationListCombined);

  const isLoading = isPublicationListLoading || isFilterCityLoading;

  const data = useMemo(() => {
    if (!combinedWithCity) return [];
    if (filters.length === 0) return combinedWithCity;
    return combinedWithCity?.filter((publication) =>
      filters.every((filterFn) => filterFn(publication)),
    );
  }, [combinedWithCity, filters]);

  return { data, isLoading };
};

export const getCombinedConfig = (config) => {
  const queryClient = useQueryClient();
  return {
    ...config,
    onSuccess: (data) => {
      queryClient.invalidateQueries();
      window.localStorage.removeItem('REACT_QUERY_OFFLINE_CACHE');
      config?.onSuccess?.(data);
    },
  };
};

export const useEstablishmentsListFilterCity = (cityCode?: string) => {
  const { data: establishmentsList, ...query } = useEstablishmentsList();

  const result = useMemo(() => {
    if (!cityCode) return establishmentsList;
    return establishmentsList?.filter((e) => e?.city?.code === cityCode);
  }, [establishmentsList, cityCode]);

  return { data: result, ...query };
};

const usePublicationWityCityCode = (
  publicationList: Publication[],
  establishmentsList: Establishment[],
) => {
  const { data: publicationEstablishments, isLoading } =
    usePublicationEstablishmentsList();

  const result = useMemo(() => {
    return publicationList?.map((p) => {
      const establishmentsIds = publicationEstablishments
        ?.filter((e) => e?.publication_id === p?.id)
        .map((e) => e.establishment_id);

      const cityCodes = establishmentsList
        ?.filter((e) => establishmentsIds?.includes(e?.id))
        .map((e) => e?.city?.code);

      return { ...p, city_codes: cityCodes };
    });
  }, [publicationList, establishmentsList, publicationEstablishments]);

  return { data: result, isLoading };
};

export const usePublicationFromCity = (publicationList: Publication[]) => {
  const { query: currentQuery } = useFilterQueryParams();
  const cityCode = currentQuery?.city as string;
  const {
    data: establishmentsFilterCity,
    isLoading: isEstablishmentsListByCityLoading,
  } = useEstablishmentsListFilterCity(cityCode);
  const {
    data: publicationFromEstablishments,
    isLoading: isPublicationListFromEstablishmentsLoading,
  } = usePublicationWityCityCode(publicationList, establishmentsFilterCity);

  const isLoading =
    isEstablishmentsListByCityLoading ||
    isPublicationListFromEstablishmentsLoading;

  return { data: publicationFromEstablishments, isLoading };
};

const usePublicatiosListWithGeolocation = (publications: Publication[]) => {
  const { data: publicationEstablishments, isLoading } =
    usePublicationEstablishmentsListComposedGeolocation();

  const publicationsWithDistance = useMemo(() => {
    if (!publications || !publicationEstablishments) return [];

    return publications.map((publication) => {
      const establishment = publicationEstablishments.find(
        (pe) => pe.publication_id === publication.id,
      );

      return {
        ...publication,
        distance: establishment?.establishment?.distance ?? null,
      };
    });
  }, [publications, publicationEstablishments]);

  const filteredAndOrderedPublications = useMemo(() => {
    if (!publicationEstablishments) return [];

    const publicationIds = publicationEstablishments.map(
      (e) => e.publication_id,
    );

    return publicationsWithDistance
      .filter((p) => publicationIds.includes(p.id))
      .sort((a, b) => a.distance - b.distance);
  }, [publicationsWithDistance, publicationEstablishments]);

  return {
    data: filteredAndOrderedPublications,
    publicationsWithDistance,
    isLoading,
  };
};

export const usePublicationsFeaturedListComposedGeolocation = () => {
  const { data: publications, isLoading: isLoadingPublications } =
    usePublicationFeaturedList();
  const { data: filteredPublications, isLoading: isLoadingEstablishments } =
    usePublicatiosListWithGeolocation(publications);

  const allFeaturedList = useMemo(() => {
    return [
      ...(publications?.filter((p) => p.is_global) || []),
      ...(filteredPublications || []),
    ];
  }, [filteredPublications, publications]);

  const isEmpty = allFeaturedList?.length === 0;
  const isLoading = isLoadingPublications || isLoadingEstablishments;

  return { data: allFeaturedList, isLoading, isEmpty };
};

export const usePublicationsAvailableListComposedGeolocation = () => {
  const { data: publications, isLoading: isLoadingPublications } =
    usePublicationAvailableList();
  const { data: publicationsFiltered, isLoading: isLoadingEstablishments } =
    usePublicatiosListWithGeolocation(publications);

  const isLoading = isLoadingPublications || isLoadingEstablishments;
  const isEmpty = publicationsFiltered.length === 0;

  return { data: publicationsFiltered, isLoading, isEmpty };
};

const usePublicationsAvailableNearbyList = (maxDistance = 5000) => {
  const { data: publicationsWithDistance, isLoading } =
    usePublicationsAvailableListComposedGeolocation();

  const nearbyPublications = useMemo(() => {
    return publicationsWithDistance.filter(
      (publication) => publication.distance <= maxDistance,
    );
  }, [publicationsWithDistance, maxDistance]);

  const isEmpty = nearbyPublications.length === 0;

  return {
    data: nearbyPublications,
    isLoading,
    isEmpty,
  };
};

export const usePublicationAvailableGlobalList = () => {
  const { data: publicationList, isLoading } = usePublicationAvailableList();

  const result = useMemo(() => {
    return publicationList?.filter((p) => p.is_global) ?? [];
  }, [publicationList]);

  const isEmpty = result?.length === 0;

  return { data: result, isLoading, isEmpty };
};

export const usePublicationAvailableComposedList = () => {
  const { query: currentQuery } = useFilterQueryParams();
  const queryPublicationsWithGeolocation =
    usePublicationsAvailableListComposedGeolocation();
  const queryPublicationsGlobal = usePublicationAvailableGlobalList();
  const queryPublicationsNearby = usePublicationsAvailableNearbyList();

  const queryAllPublications = useMemo(() => {
    return [
      ...(queryPublicationsWithGeolocation?.data || []),
      ...(queryPublicationsGlobal?.data || []),
    ];
  }, [queryPublicationsWithGeolocation.data, queryPublicationsGlobal.data]);

  const isLoading =
    queryPublicationsGlobal?.isLoading ||
    queryPublicationsWithGeolocation?.isLoading;

  const strategy = useMemo(() => {
    if (currentQuery?.is_nearby) return queryPublicationsNearby;

    if (currentQuery?.is_global) return queryPublicationsGlobal;

    return { data: queryAllPublications, isLoading };
  }, [
    currentQuery,
    queryPublicationsNearby,
    queryPublicationsGlobal,
    queryAllPublications,
    isLoading,
  ]);

  return strategy;
};
